import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { EventLocation } from './EventLocation';
import { Organizer } from './Organizer';
import { ResultSetup } from './ResultSetup';
import { EventSetup } from './EventSetup';
import { SignupSettings } from './SignupSettings';
import type { EntityState } from './../enums/EntityState';
import type { FunctionalityType } from './../enums/FunctionalityType';
import { formatDateIso } from '@/utilities/dateutils';

export class FocusEvent extends BaseObj {
    public ID: number;
    public __id: number;

    public Date: Date;
    public __date: Date;

    public Deleted: boolean;
    public __deleted: boolean;

    public DisciplineID: number | null;
    public __disciplineid: number | null;

    public EventEnd: Date;
    public __eventend: Date;

    public EventFirstStart: Date | null;
    public __eventfirststart: Date | null;

    public readonly EventID: number;

    public EventStart: Date;
    public __eventstart: Date;

    public FederationID: number;
    public __federationid: number;

    public Functionalities: FunctionalityType[];
    public __functionalities: FunctionalityType[];

    public Location: EventLocation | null;
    public __location: EventLocation | null;

    public Name: string;
    public __name: string;

    public NumContestants: number;
    public __numcontestants: number;

    public readonly Organizer: Organizer | null;

    public OrganizerID: number;
    public __organizerid: number;

    public ParentID: number | null;
    public __parentid: number | null;

    public ProfileID: number | null;
    public __profileid: number | null;

    public ReserveBibsFrom: number;
    public __reservebibsfrom: number;

    public ReserveBibsTo: number;
    public __reservebibsto: number;

    public readonly ResultSetup: ResultSetup | null;

    public readonly Setup: EventSetup | null;

    public readonly SignupSettings: SignupSettings | null;

    public SportID: number;
    public __sportid: number;

    public SportlevelID: number | null;
    public __sportlevelid: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Date?: Date;
        Deleted?: boolean;
        DisciplineID?: number | null;
        EntityState?: EntityState;
        EventEnd?: Date;
        EventFirstStart?: Date | null;
        EventID?: number;
        EventStart?: Date;
        FederationID?: number;
        Functionalities?: FunctionalityType[];
        Location?: EventLocation | null;
        Name?: string | null;
        NumContestants?: number;
        Organizer?: Organizer | null;
        OrganizerID?: number;
        ParentID?: number | null;
        ProfileID?: number | null;
        ReserveBibsFrom?: number;
        ReserveBibsTo?: number;
        ResultSetup?: ResultSetup | null;
        Setup?: EventSetup | null;
        SignupSettings?: SignupSettings | null;
        SportID?: number;
        SportlevelID?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<FocusEvent>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('FocusEvent tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Date = values.Date ?? new Date('1970-01-01T00:00:00.000');
        this.__date = values.Date ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'Date', {
            get(): Date {
                return this.__date;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('Date', this.__date, value);
                this.__date = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Deleted = values.Deleted ?? false;
        this.__deleted = values.Deleted ?? false;
        Object.defineProperty(this, 'Deleted', {
            get(): boolean {
                return this.__deleted;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Deleted', this.__deleted, value);
                this.__deleted = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DisciplineID = values.DisciplineID ?? null;
        this.__disciplineid = values.DisciplineID ?? null;
        Object.defineProperty(this, 'DisciplineID', {
            get(): number | null {
                return this.__disciplineid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('DisciplineID', this.__disciplineid, value);
                this.__disciplineid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventEnd = values.EventEnd ?? new Date('1970-01-01T00:00:00.000');
        this.__eventend = values.EventEnd ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'EventEnd', {
            get(): Date {
                return this.__eventend;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('EventEnd', this.__eventend, value);
                this.__eventend = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventFirstStart = values.EventFirstStart ?? null;
        this.__eventfirststart = values.EventFirstStart ?? null;
        Object.defineProperty(this, 'EventFirstStart', {
            get(): Date | null {
                return this.__eventfirststart;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('EventFirstStart', this.__eventfirststart, value);
                this.__eventfirststart = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;

        this.EventStart = values.EventStart ?? new Date('1970-01-01T00:00:00.000');
        this.__eventstart = values.EventStart ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'EventStart', {
            get(): Date {
                return this.__eventstart;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('EventStart', this.__eventstart, value);
                this.__eventstart = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.FederationID = values.FederationID ?? 0;
        this.__federationid = values.FederationID ?? 0;
        Object.defineProperty(this, 'FederationID', {
            get(): number {
                return this.__federationid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('FederationID', this.__federationid, value);
                this.__federationid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Functionalities = values.Functionalities ?? [];
        this.__functionalities = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.Functionalities ?? [],
            callback: (oldVal, newVal) => this.__entityStateHandler.propertyChanged('Functionalities', oldVal, newVal),
        });
        Object.defineProperty(this, 'Functionalities', {
            get(): FunctionalityType[] {
                return this.__functionalities;
            },
            set(value: FunctionalityType[]) {
                this.__entityStateHandler.propertyChanged('Functionalities', this.__functionalities, value);
                this.__functionalities = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (oldVal: FunctionalityType[], newVal: FunctionalityType[]) =>
                        this.__entityStateHandler.propertyChanged('Functionalities', oldVal, newVal),
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.Location = values.Location ?? null;
        this.__location = this.__entityStateHandler.createProxyWrapperWithMutationTracking({
            childObject: values.Location ?? null,
            childField: 'Location',
        });
        Object.defineProperty(this, 'Location', {
            get(): EventLocation | null {
                return this.__location;
            },
            set(value: EventLocation | null) {
                this.__location = this.__entityStateHandler.createProxyWrapperWithMutationTracking({
                    childObject: value,
                    childField: 'Location',
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NumContestants = values.NumContestants ?? 0;
        this.__numcontestants = values.NumContestants ?? 0;
        Object.defineProperty(this, 'NumContestants', {
            get(): number {
                return this.__numcontestants;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('NumContestants', this.__numcontestants, value);
                this.__numcontestants = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Organizer = values.Organizer ?? null;
        this.Organizer = values.Organizer ?? null;
        this.Organizer?._setNotifyParentOfModificationCallback(() =>
            this.__entityStateHandler.childBaseObjChanged('Organizer', this.Organizer),
        );

        this.OrganizerID = values.OrganizerID ?? 0;
        this.__organizerid = values.OrganizerID ?? 0;
        Object.defineProperty(this, 'OrganizerID', {
            get(): number {
                return this.__organizerid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('OrganizerID', this.__organizerid, value);
                this.__organizerid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ParentID = values.ParentID ?? null;
        this.__parentid = values.ParentID ?? null;
        Object.defineProperty(this, 'ParentID', {
            get(): number | null {
                return this.__parentid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ParentID', this.__parentid, value);
                this.__parentid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ProfileID = values.ProfileID ?? null;
        this.__profileid = values.ProfileID ?? null;
        Object.defineProperty(this, 'ProfileID', {
            get(): number | null {
                return this.__profileid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ProfileID', this.__profileid, value);
                this.__profileid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ReserveBibsFrom = values.ReserveBibsFrom ?? 0;
        this.__reservebibsfrom = values.ReserveBibsFrom ?? 0;
        Object.defineProperty(this, 'ReserveBibsFrom', {
            get(): number {
                return this.__reservebibsfrom;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ReserveBibsFrom', this.__reservebibsfrom, value);
                this.__reservebibsfrom = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ReserveBibsTo = values.ReserveBibsTo ?? 0;
        this.__reservebibsto = values.ReserveBibsTo ?? 0;
        Object.defineProperty(this, 'ReserveBibsTo', {
            get(): number {
                return this.__reservebibsto;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ReserveBibsTo', this.__reservebibsto, value);
                this.__reservebibsto = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ResultSetup = values.ResultSetup ?? null;
        this.ResultSetup = values.ResultSetup ?? null;
        this.ResultSetup?._setNotifyParentOfModificationCallback(() =>
            this.__entityStateHandler.childBaseObjChanged('ResultSetup', this.ResultSetup),
        );

        this.Setup = values.Setup ?? null;
        this.Setup = values.Setup ?? null;
        this.Setup?._setNotifyParentOfModificationCallback(() => this.__entityStateHandler.childBaseObjChanged('Setup', this.Setup));

        this.SignupSettings = values.SignupSettings ?? null;
        this.SignupSettings = values.SignupSettings ?? null;
        this.SignupSettings?._setNotifyParentOfModificationCallback(() =>
            this.__entityStateHandler.childBaseObjChanged('SignupSettings', this.SignupSettings),
        );

        this.SportID = values.SportID ?? 0;
        this.__sportid = values.SportID ?? 0;
        Object.defineProperty(this, 'SportID', {
            get(): number {
                return this.__sportid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('SportID', this.__sportid, value);
                this.__sportid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SportlevelID = values.SportlevelID ?? null;
        this.__sportlevelid = values.SportlevelID ?? null;
        Object.defineProperty(this, 'SportlevelID', {
            get(): number | null {
                return this.__sportlevelid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('SportlevelID', this.__sportlevelid, value);
                this.__sportlevelid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Date": ${'"' + formatDateIso(this.Date).substring(0, 10) + '"'}` +
            ',' +
            `"Deleted": ${JSON.stringify(this.Deleted)}` +
            ',' +
            `"DisciplineID": ${JSON.stringify(this.DisciplineID)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventEnd": ${'"' + formatDateIso(this.EventEnd) + '"'}` +
            ',' +
            `"EventFirstStart": ${this.EventFirstStart ? '"' + formatDateIso(this.EventFirstStart) + '"' : null}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"EventStart": ${'"' + formatDateIso(this.EventStart) + '"'}` +
            ',' +
            `"FederationID": ${JSON.stringify(this.FederationID)}` +
            ',' +
            `"Functionalities": [ ${this.Functionalities.map((x) => (x == 'None' ? 0 : x == 'Shooting' ? 1 : 0))} ]` +
            ',' +
            `"Location": ${!this.Location ? null : this.Location?.serialize()}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"NumContestants": ${JSON.stringify(this.NumContestants)}` +
            ',' +
            `"Organizer": ${!this.Organizer ? null : this.Organizer?.serialize()}` +
            ',' +
            `"OrganizerID": ${JSON.stringify(this.OrganizerID)}` +
            ',' +
            `"ParentID": ${JSON.stringify(this.ParentID)}` +
            ',' +
            `"ProfileID": ${JSON.stringify(this.ProfileID)}` +
            ',' +
            `"ReserveBibsFrom": ${JSON.stringify(this.ReserveBibsFrom)}` +
            ',' +
            `"ReserveBibsTo": ${JSON.stringify(this.ReserveBibsTo)}` +
            ',' +
            `"ResultSetup": ${!this.ResultSetup ? null : this.ResultSetup?.serialize()}` +
            ',' +
            `"Setup": ${!this.Setup ? null : this.Setup?.serialize()}` +
            ',' +
            `"SignupSettings": ${!this.SignupSettings ? null : this.SignupSettings?.serialize()}` +
            ',' +
            `"SportID": ${JSON.stringify(this.SportID)}` +
            ',' +
            `"SportlevelID": ${JSON.stringify(this.SportlevelID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of FocusEvent from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Date?: Date;
        Deleted?: boolean;
        DisciplineID?: number | null;
        EventEnd?: Date;
        EventFirstStart?: Date | null;
        EventStart?: Date;
        FederationID?: number;
        Functionalities?: FunctionalityType[];
        Location?: EventLocation | null;
        Name?: string | null;
        NumContestants?: number;
        Organizer?: Organizer | null;
        OrganizerID?: number;
        ParentID?: number | null;
        ProfileID?: number | null;
        ReserveBibsFrom?: number;
        ReserveBibsTo?: number;
        ResultSetup?: ResultSetup | null;
        Setup?: EventSetup | null;
        SignupSettings?: SignupSettings | null;
        SportID?: number;
        SportlevelID?: number | null;
        EntityState?: 'Added' | 'Unchanged';
    }): FocusEvent {
        return new FocusEvent(values);
    }

    public static _FromServerJson(json: any): FocusEvent {
        return new FocusEvent({
            ID: json.ID,
            Date: json.Date instanceof Date ? json.Date : new Date(json.Date + ' 00:00:00'),
            Deleted: json.Deleted,
            DisciplineID: json.DisciplineID ?? null,
            EventEnd: json.EventEnd instanceof Date ? json.EventEnd : new Date(json.EventEnd),
            EventFirstStart: json.EventFirstStart
                ? json.EventFirstStart instanceof Date
                    ? json.EventFirstStart
                    : new Date(json.EventFirstStart)
                : null,
            EventID: json.EventID,
            EventStart: json.EventStart instanceof Date ? json.EventStart : new Date(json.EventStart),
            FederationID: json.FederationID,
            Functionalities: json.Functionalities?.map((x: any) => (x == 0 ? 'None' : x == 1 ? 'Shooting' : 'None')),
            Location: json.Location ? EventLocation._FromServerJson(json.Location) : null,
            Name: json.Name ?? '',
            NumContestants: json.NumContestants,
            Organizer: json.Organizer ? Organizer._FromServerJson(json.Organizer) : null,
            OrganizerID: json.OrganizerID,
            ParentID: json.ParentID ?? null,
            ProfileID: json.ProfileID ?? null,
            ReserveBibsFrom: json.ReserveBibsFrom,
            ReserveBibsTo: json.ReserveBibsTo,
            ResultSetup: json.ResultSetup ? ResultSetup._FromServerJson(json.ResultSetup) : null,
            Setup: json.Setup ? EventSetup._FromServerJson(json.Setup) : null,
            SignupSettings: json.SignupSettings ? SignupSettings._FromServerJson(json.SignupSettings) : null,
            SportID: json.SportID,
            SportlevelID: json.SportlevelID ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of FocusEvent from another instance of FocusEvent.
     * @param other The FocusEvent you want to clone
     * @returns A cloned FocusEvent
     */
    public static FromOther(other: FocusEvent): FocusEvent {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return FocusEvent._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return FocusEvent.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'Date'
            | 'Deleted'
            | 'DisciplineID'
            | 'EventEnd'
            | 'EventFirstStart'
            | 'EventID'
            | 'EventStart'
            | 'FederationID'
            | 'Functionalities'
            | 'Location'
            | 'Name'
            | 'NumContestants'
            | 'Organizer'
            | 'OrganizerID'
            | 'ParentID'
            | 'ProfileID'
            | 'ReserveBibsFrom'
            | 'ReserveBibsTo'
            | 'ResultSetup'
            | 'Setup'
            | 'SignupSettings'
            | 'SportID'
            | 'SportlevelID'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'FocusEvent';
}
